import { motion } from "framer-motion";
import { Users, Wifi, Snowflake, Briefcase, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

const featureIcons: Record<string, typeof Wifi> = {
  "A/C": Snowflake,
  "Wi-Fi": Wifi,
  "Luggage Space": Briefcase,
  "Extra Luggage": Briefcase,
  "Group Seating": Users,
  "Large Capacity": Users,
};

const FleetSection = () => {
  const { data: vehicles, isLoading } = useQuery({
    queryKey: ["fleet-home-vehicles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicles")
        .select("*")
        .eq("is_active", true)
        .order("sort_order")
        .limit(3);
      if (error) throw error;
      return data;
    },
  });

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-14"
        >
          <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-2">
            Our Fleet
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground">
            Choose Your Vehicle
          </h2>
        </motion.div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg overflow-hidden border border-border">
                <Skeleton className="h-48 w-full" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {vehicles?.map((vehicle, index) => (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white rounded-lg overflow-hidden border border-border hover:shadow-md transition-all duration-300"
              >
                <div className="relative overflow-hidden h-48">
                  <img
                    src={vehicle.image_url || "/placeholder.svg"}
                    alt={vehicle.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute top-3 right-3 bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-md">
                    <Users className="w-3 h-3 inline mr-1" />
                    {vehicle.min_passengers}-{vehicle.capacity} pax
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-bold text-foreground mb-2">{vehicle.name}</h3>
                  {vehicle.description && (
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">{vehicle.description}</p>
                  )}
                  {vehicle.features && vehicle.features.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {vehicle.features.map((f) => {
                        const Icon = featureIcons[f] || Wifi;
                        return (
                          <span key={f} className="inline-flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-md">
                            <Icon className="w-3 h-3" /> {f}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link to="/fleet">
            <Button size="lg" className="gap-2 font-semibold rounded-lg px-8 bg-primary text-primary-foreground hover:bg-primary/90">
              View Full Fleet <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FleetSection;
